import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe, ChevronDown, Lock } from "lucide-react";
import Logo from "./Logo";
import { useI18n, LANGUAGES } from "../i18n";

const Navbar = () => {
  const { t, lang, setLang } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setLangOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const links = [
    { id: "home", label: t.nav.home },
    { id: "about", label: t.nav.about },
    { id: "services", label: t.nav.services },
    { id: "portfolio", label: t.nav.portfolio },
    { id: "whyus", label: t.nav.whyus },
  ];

  const onNav = (id) => (e) => {
    if (location.pathname !== "/") {
      // Cross-page: let browser handle href "/#id" navigation; just close menu
      setOpen(false);
      return;
    }
    // Same-page: close menu first, THEN scroll after AnimatePresence exit finishes (~300ms)
    e.preventDefault();
    setOpen(false);
    setTimeout(() => {
      if (id === "home") {
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      const el = document.getElementById(id);
      if (el) {
        const y = el.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 350);
  };

  const currentLang = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[var(--bg)]/85 backdrop-blur-xl border-b border-[var(--border)]" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link"><Logo size={36} /></Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.id}
              href={location.pathname === "/" ? `#${l.id}` : `/#${l.id}`}
              onClick={onNav(l.id)}
              data-testid={`nav-${l.id}`}
              className="text-sm tracking-wide text-[var(--text-main)] hover:text-white transition-colors relative group"
            >
              {l.label}
              <span className="absolute left-0 -bottom-1 h-px w-0 bg-white transition-all group-hover:w-full" />
            </a>
          ))}
          <Link
            to="/marketing"
            data-testid="nav-marketing"
            className="text-sm tracking-wide text-[var(--text-main)] hover:text-white transition-colors relative group"
          >
            Marketing
            <span className="absolute left-0 -bottom-1 h-px w-0 bg-white transition-all group-hover:w-full" />
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          {/* Language dropdown */}
          <div ref={langRef} className="hidden sm:block relative">
            <button
              onClick={() => setLangOpen((o) => !o)}
              data-testid="lang-toggle"
              className="flex items-center gap-2 px-3 py-2 border border-[var(--border)] text-xs tracking-widest text-[var(--text-main)] hover:border-white hover:text-white transition-colors"
            >
              <Globe size={14} />
              {currentLang.code.toUpperCase()}
              <ChevronDown size={12} className={langOpen ? "rotate-180 transition-transform" : "transition-transform"} />
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-2 w-48 bg-[var(--surface)] border border-[var(--border)] py-2 shadow-xl"
                  data-testid="lang-menu"
                >
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      data-testid={`lang-option-${l.code}`}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-[var(--bg)] transition-colors flex items-center justify-between ${
                        l.code === lang ? "text-white" : "text-[var(--text-muted)]"
                      }`}
                    >
                      <span>{l.label}</span>
                      <span className="text-[10px] tracking-widest">{l.code.toUpperCase()}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <a href="#contact" onClick={onNav("contact")} data-testid="nav-cta-button" className="hidden md:inline-flex btn-sharp btn-primary !py-3 !px-5 !text-xs">
            {t.nav.contact}
          </a>

          {/* Admin login icon button with hover tooltip */}
          <Link
            to="/admin/login"
            data-testid="nav-admin-button"
            aria-label={t.nav.adminLogin}
            className="hidden md:inline-flex relative group items-center justify-center w-10 h-10 border border-[var(--border)] text-[var(--text-muted)] hover:text-white hover:border-white transition-colors"
          >
            <Lock size={14} strokeWidth={1.75} />
            <span className="pointer-events-none absolute top-full right-0 mt-2 whitespace-nowrap bg-[var(--surface)] border border-[var(--border)] text-white text-[11px] tracking-widest uppercase px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity">
              {t.nav.adminLogin}
            </span>
          </Link>

          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden relative z-[60] p-3 -mr-3 text-white min-w-[48px] min-h-[48px] flex items-center justify-center active:bg-white/10 rounded-md"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            aria-expanded={open}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[var(--bg)] border-t border-[var(--border)] overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <a
                  key={l.id}
                  href={location.pathname === "/" ? `#${l.id}` : `/#${l.id}`}
                  onClick={onNav(l.id)}
                  data-testid={`mobile-nav-${l.id}`}
                  className="text-base text-white tracking-wide"
                >
                  {l.label}
                </a>
              ))}
              <Link to="/marketing" onClick={() => setOpen(false)} data-testid="mobile-nav-marketing" className="text-base text-white tracking-wide">
                Marketing
              </Link>
              <a
                href={location.pathname === "/" ? "#contact" : "/#contact"}
                onClick={onNav("contact")}
                data-testid="mobile-nav-contact"
                className="text-base text-white tracking-wide"
              >
                {t.nav.contact}
              </a>
              <div className="flex flex-wrap gap-2 mt-3">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    data-testid={`mobile-lang-${l.code}`}
                    className={`px-3 py-1.5 border text-xs tracking-widest ${
                      l.code === lang ? "bg-white text-black border-white" : "border-[var(--border)] text-white"
                    }`}
                  >
                    {l.code.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
